import { makeStyles } from '@material-ui/core/styles';

import React from 'react';

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Col,
  DataGrid,
  Divider,
  Link,
  Paper,
  Row,
  TextField,
  Typography,
  WorkflowViewContainer,
  defaultPaginationData,
} from 'components/AORedesign';
import { Feature } from 'components/Feature';

import cuid from 'cuid';

import axios from 'axios';

import { SEARCH_AGENT } from 'constants/api';
import featureFlags from 'constants/featureFlags';
import { useState } from 'react';
import { handleOnChange } from '../../CommonUtilityServices/EventHandlerService';
import AddOrUpdateAgent from './AddOrUpdateAgent';

const EDIT_USER_LABEL = 'Search/Edit Agent';
const defaultFormData = { code: '', loc_name: '' };

const useRowStyles = makeStyles({
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
  container: {
    maxHeight: '400px',
    overflow: 'auto',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
});
const SearchAgent = () => {
  const noResultsMessage = 'No results found. Adjust your filters.';
  const classes = useRowStyles();
  const [searchResults, setSearchResults] = useState(undefined);
  const [searchLoading, setSearchLoading] = useState(false);
  const [formData, setFormData] = useState(defaultFormData);
  const [paginationData, setPaginationData] = useState(defaultPaginationData);

  const handleSearchOnClick = async () => {
    setSearchLoading(true);

    setSearchResults(undefined);
    try {
      const response = await axios.get(`${SEARCH_AGENT}`, { params: formData });
      if (response.status === 200) {
        setSearchResults(response.data.map((agency) => ({ ...agency, id: cuid() })));
        setPaginationData({ ...paginationData, totalCount: response.data.length, currentPage: 0 });
      } else if (response.status === 204) {
        setSearchResults([]);
      }
    } catch (error) {
      console.error(error);
      setSearchResults([]);
    }
    setSearchLoading(false);
  };
  const renderCell = (params) => {
    const TriggerButton = (props) => <Link {...props}>{params.row?.code.trim()}</Link>;
    return <AddOrUpdateAgent triggerButton={TriggerButton} agent={params.row} resetSearchResults={handleSearchOnClick} />;
  };

  const searchResultColumns = [
    {
      field: 'code',
      headerName: 'Code',
      flex: 0.5,
      renderCell,
    },
    {
      field: 'locname',
      headerName: 'Name',
      flex: 0.75,
    },
    {
      field: 'city',
      headerName: 'City',
      flex: 0.5,
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 0.5,
    },
  ];

  const handleClearOnClick = () => {
    setFormData(defaultFormData);
    setSearchResults(undefined);
  };

  const TriggerButton = (props) => (
    <Link {...props} startIcon={'editOutlined'}>
      {EDIT_USER_LABEL}
    </Link>
  );

  // handle pagination
  const handleOnPageChange = (page) => {
    setPaginationData((prevState) => ({ ...prevState, currentPage: page }));
  };
  const handleOnPageSizeChange = (pageSize) => {
    setPaginationData({ pageSize: pageSize });
  };

  const handleResetOnClose = () => {
    setFormData(defaultFormData);
    setSearchResults([]);
  };

  return (
    <Feature allowed={featureFlags.ACCESS_FIND_AGENT}>
      <WorkflowViewContainer appBarTitle={EDIT_USER_LABEL} TriggerButton={TriggerButton} handleResetOnClose={handleResetOnClose}>
        <Box mx={9} my={5}>
          <Card>
            <Box mb={4}>
              <Typography variant="h5">{'Search for Agent'}</Typography>
            </Box>
            <Paper variant="outlined" square style={{ boxSizing: 'border-box', height: '100%' }}>
              <Box ml={2} mt={2}>
                <Row mb={4}>
                  <Typography variant="body1">This form allows you to search for an agent.</Typography>
                </Row>
                <Box p={1} />
                <Row>
                  <Col xs={4}>
                    <Box mb={2}>
                      <TextField
                        data-testid="search-agent-agency-code"
                        name={'code'}
                        value={formData.agencyCode}
                        onChange={(e) => handleOnChange(e, formData, setFormData)}
                        label={'Agency Code'}
                        placeholder={'Enter Agency Code'}
                        style={{ width: 350 }}
                      />
                    </Box>
                    <Box mb={2}>
                      <TextField
                        id={'search-agent-agency-name'}
                        name={'loc_name'}
                        value={formData.agencyName}
                        onChange={(e) => handleOnChange(e, formData, setFormData)}
                        label={'Agency Name'}
                        placeholder={'Enter Agency Name'}
                        style={{ width: 350 }}
                      />
                    </Box>
                  </Col>
                  <Col xs={4}>
                    <Typography color={'textSecondary'}>
                      The search function has a wildcard at the end of the term submitted. To find agent with agency code{' '}
                      <Box display={'inline'} fontWeight={'bold'}>
                        12345
                      </Box>{' '}
                      you could either.
                    </Typography>
                    <Box mb={2} />
                    <Typography>
                      Enter{' '}
                      <Box display={'inline'} fontWeight={'bold'}>
                        {`"123"`}
                      </Box>{' '}
                      in the Agency Code field.
                    </Typography>
                    <Typography>
                      Enter{' '}
                      <Box display={'inline'} fontWeight={'bold'}>
                        {`"Jo"`}
                      </Box>{' '}
                      in the Agency Name field.
                    </Typography>
                  </Col>
                  <Col xs={4} ml={4}>
                    <Box ml={10}>
                      <Button
                        variant={'contained'}
                        color={'primary'}
                        id={'search-agent-button'}
                        onClick={handleSearchOnClick}
                        loading={searchLoading}
                        disabled={searchLoading || (formData.code.length < 2 && formData.loc_name.length < 2)}>
                        Search
                        {searchLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
                      </Button>

                      <Box display={'inline'} mr={2} />
                      <Button variant={'outlined'} color={'primary'} onClick={handleClearOnClick} id={'search-user-clear-button'}>
                        Clear
                      </Button>
                    </Box>
                  </Col>
                </Row>
              </Box>
            </Paper>
            {searchResults && (
              <Box mt={6}>
                <Divider />
                <Box display={'flex'} justifyContent="space-between" alignItems="center" my={2}>
                  <Typography variant="h6">Search Result </Typography>
                </Box>

                <Box flex={1} height="fit-content">
                  <DataGrid
                    columns={searchResultColumns}
                    loading={searchLoading}
                    onPageChange={handleOnPageChange}
                    onPageSizeChange={handleOnPageSizeChange}
                    page={paginationData.currentPage}
                    pageSize={paginationData.pageSize}
                    paginationMode="client"
                    rowCount={paginationData.totalCount}
                    rows={searchResults}
                    noResultsMessage={noResultsMessage}
                    autoHeight
                    autoWidth={true}
                  />
                </Box>
              </Box>
            )}
          </Card>
        </Box>
      </WorkflowViewContainer>
    </Feature>
  );
};

export default SearchAgent;
